import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';

import { AUTH_ERROR_RELOGIN_KEY } from '#screens/error-boundaries/unauthenticated-error-display.js';
import { useTRPC } from '#src/trpc.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';
import { useAuth0Context } from '#state/contexts/auth0-context.js';

import type { Auth0AppState } from '#utils/auth0-app-state.js';

const RELOGIN_ATTEMPTED_KEY = 'cadstrom:relogin-attempted';

/**
 * Hook to initialize the current organization ID if not set.
 */
export function useInitializeCurrentOrganization() {
	const { projectId, orgName } = useParams({ strict: false });
	const trpc = useTRPC();
	const auth0 = useAuth0Context();
	const posthog = usePostHog();

	const [lastOpenProject, setLastOpenProject] = useAtom(lastOpenProjectAtom);
	const currentProjectId = projectId ?? lastOpenProject.projectId;

	const {
		data: currentUser,
		isLoading: currentUserLoading,
		isSuccess: isCurrentUserSuccess,
	} = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, {
			throwOnError: false,
		}),
	);
	const { data: projectPreview, isLoading: projectPreviewLoading } = useQuery(
		trpc.project.getPreview.queryOptions(
			{ id: currentProjectId ?? '' },
			{
				enabled: !!currentProjectId,
				throwOnError: false,
			},
		),
	);

	useEffect(() => {
		if (!currentUser) {
			return;
		}
		posthog.identify(currentUser.actor.userId, {
			email: currentUser.actor.email,
			name: currentUser.actor.displayName,
		});
	}, [currentUser, posthog]);

	// Group events under the organization being viewed, falling back to the
	// last-open org and then the first membership. Staff viewing a foreign org
	// resolve to their own (staff) membership rather than the customer org.
	const currentOrgName = orgName ?? lastOpenProject.orgName;
	useEffect(() => {
		if (!currentUser) {
			return;
		}
		const membership =
			currentUser.memberships.find((m) => m.groupUsername === currentOrgName) ?? currentUser.memberships[0];
		if (!membership) {
			return;
		}
		posthog.group('organization', membership.groupId, {
			name: membership.groupName,
			username: membership.groupUsername,
		});
	}, [currentUser, currentOrgName, posthog]);

	// Clear the one-shot auth-error relogin guard only on a confirmed fresh success.
	// React Query keeps stale `data` from a prior success across a failed (401)
	// refetch, so truthy `currentUser` alone wouldn't prove the current request
	// succeeded — clearing on that could wipe the guard while auth is still broken
	// and re-enable the redirect loop.
	useEffect(() => {
		if (isCurrentUserSuccess) {
			sessionStorage.removeItem(AUTH_ERROR_RELOGIN_KEY);
		}
	}, [isCurrentUserSuccess]);

	// If the user is authenticated but has no memberships (e.g. due to a stale token refresh losing the Auth0 org
	// claim), force a full re-login so the Auth0 Post-Login Action runs and adds the org claim to the fresh token.
	// A sessionStorage flag prevents infinite redirect loops.
	useEffect(() => {
		if (currentUserLoading || !currentUser) {
			return;
		}

		if (currentUser.memberships.length > 0) {
			sessionStorage.removeItem(RELOGIN_ATTEMPTED_KEY);
			return;
		}

		if (sessionStorage.getItem(RELOGIN_ATTEMPTED_KEY)) {
			return;
		}

		sessionStorage.setItem(RELOGIN_ATTEMPTED_KEY, 'true');
		void auth0.client.loginWithRedirect<Auth0AppState>({
			appState: {
				returnTo: window.location.pathname + window.location.search,
			},
		});
	}, [currentUser, currentUserLoading, auth0.client]);

	// Default the "last open project" org once user/project data has loaded. This
	// runs in an effect, not during render: it writes a shared atom that other
	// mounted components (e.g. the org switcher) read, and writing mid-render
	// triggers a cross-component update warning — reachable on first login, when
	// no org has been selected yet.
	useEffect(() => {
		if (currentUserLoading || projectPreviewLoading || lastOpenProject.orgName) {
			return;
		}

		if (currentProjectId === null) {
			// If there is no project open, default to the first organization the user is a member of
			const firstMembership = currentUser?.memberships[0];
			if (firstMembership) {
				setLastOpenProject((prev) => ({ ...prev, orgName: firstMembership.groupUsername }));
			}
		} else if (
			projectPreview?.projectPreview.ownerId !== null &&
			projectPreview?.projectPreview.ownerId !== undefined
		) {
			// If there is a project open, find the org name by matching the project's owner against the user's memberships
			const ownerMembership = currentUser?.memberships.find((m) => m.groupId === projectPreview.projectPreview.ownerId);
			if (ownerMembership) {
				setLastOpenProject((prev) => ({ ...prev, orgName: ownerMembership.groupUsername }));
			}
		}
	}, [
		currentUserLoading,
		projectPreviewLoading,
		lastOpenProject.orgName,
		currentProjectId,
		currentUser,
		projectPreview,
		setLastOpenProject,
	]);
}
